import classNames from "classnames";
import { motion } from "framer-motion";
import { ReactNode, useRef, useState } from "react";
import getNextItem from "./getNextItem";
import SwitchLabel from "./SwitchLabel";

// TODO: make it not animate when the position of the switch changes
// right now it slowly moves to the new position anytime something changes the layout of the page

export interface SwitchItem<Value extends string> {
  label?:
    | {
        mobile: string;
        desktop: string;
      }
    | string;
  value: Value;
  icon?: (props: { className: string }) => ReactNode;
}

interface Props<Value extends string> {
  items: SwitchItem<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  wholeButtonToggle?: boolean;
  size?: "large" | "base";
}

export default function Switch<V extends string>({
  items,
  value,
  onChange,
  wholeButtonToggle,
  size = "base",
}: Props<V>) {
  const id = useRef(Math.random().toString(36).substring(7));

  return (
    <div
      className={classNames(
        "z-0 mb-[2px] inline-flex overflow-hidden rounded border border-slate-300 font-brik text-white",
        size === "large" && ["h-[54px]"],
        size === "base" && ["h-[37px]"]
      )}
    >
      {items.map((item, i) => {
        return (
          <div
            key={item.value}
            onClick={() => {
              const nextItem = wholeButtonToggle
                ? getNextItem<V>(items, value)
                : item;

              onChange(nextItem.value);
            }}
            className={classNames(
              "relative z-0 cursor-pointer",

              size === "large" && [
                "px-4 text-2xl",
                i === 0 && "pl-5",
                i === items.length - 1 && !item.icon && "pr-5",
              ],
              size === "base" && [
                "px-2",
                i === 0 && "pl-3",
                i === items.length - 1 && !item.icon && "pr-3",
              ]
            )}
          >
            <div
              className={classNames(
                "relative z-10 flex h-full select-none items-center transition-transform active:translate-y-0.5",
                item.value === value
                  ? "text-neutral-900"
                  : "text-white hover:text-slate-300 "
              )}
            >
              <div
                className={classNames(
                  "flex items-center",
                  item.value === value && "mb-0.5"
                )}
              >
                {item.label && <SwitchLabel label={item.label} />}

                {item.icon &&
                  item.icon({
                    className: classNames(
                      size === "base" && [
                        "h-4 w-5 mb-0.5",
                        item.label && "ml-1",
                      ],
                      size === "large" && ["h-6 w-7", item.label && "ml-2"]
                    ),
                  })}
              </div>
            </div>

            {item.value === value ? (
              <motion.div
                transition={{ duration: 0.1 }}
                layout
                className="absolute -inset-[1px] z-0 scale-95 rounded" // to maintain same border radius as the parent stroke so it fits perfectly
                initial={{ transform: "none" }}
                layoutId={id.current}
              >
                <div
                  className={classNames(
                    "absolute inset-0 rounded bg-slate-500"
                  )}
                />

                <motion.div
                  className={classNames(
                    "absolute inset-0 bottom-1 rounded bg-slate-300"
                  )}
                />
              </motion.div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
